/**
 * InstructorProfile — public-facing "meet the instructor" page hit from
 * the Lesson Tools sidebar. Renders:
 *   • Instructor identity + online status + avatar
 *   • Business context (survey-driven) and equipment
 *   • Community footprint (topics, replies, certificates)
 *   • Next live / office-hours session with a Join CTA
 *   • Message-instructor CTA that opens a DM
 *
 * Uses the existing `/api/community/users/:id/profile` and
 * `/api/live-sessions?host_id=…` endpoints so we don't add a new
 * backend surface. `/instructor/:instructorId` route is wired in App.js.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import PageHero from '../components/PageHero';
import {
  ArrowLeft, Mail, CalendarClock, MessageCircle, ExternalLink,
  Award, BookOpen, Users, Video, Loader2, MapPin, Sparkles,
  GraduationCap as GraduationIcon,
} from 'lucide-react';
import { formatDistanceToNow, parseISO, format } from 'date-fns';

const StatChip = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 rounded-2xl border border-border bg-card px-3 py-2">
    <Icon className="w-4 h-4 text-primary" />
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
      <div className="text-sm font-heading font-extrabold">{value}</div>
    </div>
  </div>
);

const InstructorProfile = () => {
  const { instructorId } = useParams();
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messaging, setMessaging] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/community/users/${instructorId}/profile`);
        if (!cancelled) setProfile(data);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || 'Instructor not found');
      }
      try {
        const { data } = await axios.get(`${API}/live-sessions`, {
          params: { host_id: instructorId, status: 'scheduled' },
        });
        if (!cancelled) setSessions((data?.sessions || data || []).slice(0, 8));
      } catch { /* silent — sessions section just stays empty */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [API, instructorId]);

  const messageInstructor = async () => {
    if (!profile?.id || profile.id === user?.id || messaging) return;
    setMessaging(true);
    try {
      const { data } = await axios.post(`${API}/dm/conversations`, {
        peer_id: profile.id,
        initial_message: `Hi ${profile.name?.split(' ')[0] || ''}, I have a question about your course.`,
      });
      const convId = data?.conversation?.id;
      navigate(convId ? `/messages/${convId}` : '/messages');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not open DM');
      setMessaging(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6">
        <div className="text-lg font-heading font-extrabold">Instructor not found</div>
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="instructor-not-found-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>
    );
  }

  const initials = (profile.name || '?').split(/\s+/).map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  const nextSession = sessions?.[0];

  return (
    <div className="min-h-screen bg-background" data-testid="instructor-profile-page">
      <PageHero
        eyebrow="Instructor"
        eyebrowIcon={GraduationIcon}
        title={profile.name || 'Instructor'}
        description={profile.location || 'FDAcademy Faculty · Office hours & scheduled live sessions'}
        testId="instructor-profile-hero"
      />

      <div className="container mx-auto max-w-4xl px-4 py-6 space-y-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="rounded-full"
          data-testid="instructor-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>

        {/* Identity card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="h-20 w-20 rounded-2xl bg-primary/15 text-primary flex items-center justify-center text-xl font-heading font-extrabold overflow-hidden flex-shrink-0">
                {profile.avatar_url
                  ? <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
                  : initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-heading text-2xl font-extrabold">{profile.name}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest font-bold mt-0.5">
                  {profile.role === 'admin' ? 'Founding instructor' : profile.role} · FDAcademy
                </div>
                {profile.location && (
                  <div className="text-sm text-foreground/85 mt-2 inline-flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> {profile.location}
                  </div>
                )}
                {profile.business_name && (
                  <div className="text-sm text-foreground/85">
                    Runs {profile.business_name}{profile.business_type ? ` — ${profile.business_type}` : ''}
                  </div>
                )}
              </div>
              {profile.id !== user?.id && (
                <Button
                  onClick={messageInstructor}
                  disabled={messaging}
                  className="rounded-full font-bold flex-shrink-0"
                  data-testid="instructor-message-btn"
                >
                  {messaging
                    ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Opening…</>
                    : <><MessageCircle className="w-4 h-4 mr-1.5" /> Message instructor</>}
                </Button>
              )}
            </div>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatChip icon={BookOpen}   label="Topics"        value={profile.topic_count || 0} />
              <StatChip icon={Users}      label="Replies"       value={profile.reply_count || 0} />
              <StatChip icon={Award}      label="Certificates"  value={profile.certificate_count || 0} />
              <StatChip icon={Sparkles}   label="Designations"  value={profile.designation_count || 0} />
            </div>
          </CardContent>
        </Card>

        {/* Office hours / live sessions */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg font-extrabold inline-flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-primary" /> Office hours & live sessions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sessions.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No scheduled office hours right now. Send a message and this instructor will reply directly.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="instructor-sessions-list">
                {sessions.map((s) => {
                  let when = '';
                  try {
                    const d = parseISO(s.scheduled_start);
                    when = `${format(d, 'EEE MMM d · h:mm a')} · ${formatDistanceToNow(d, { addSuffix: true })}`;
                  } catch { /* ignore */ }
                  return (
                    <li
                      key={s.id}
                      className="flex items-center gap-3 rounded-2xl border border-border bg-card px-3 py-3"
                    >
                      <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                        <Video className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-heading font-extrabold truncate">{s.title}</div>
                        <div className="text-[11px] text-muted-foreground">{when}</div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full font-bold flex-shrink-0"
                        onClick={() => navigate(`/live/${s.id}`)}
                        data-testid={`instructor-session-view-${s.id}`}
                      >
                        View
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
            {nextSession && (
              <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs text-primary flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Next up:</strong> {nextSession.title} —{' '}
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:no-underline"
                    onClick={() => navigate(`/live/${nextSession.id}`)}
                  >
                    open session <ExternalLink className="inline w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {profile.email && (
          <Card>
            <CardContent className="pt-6 flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4 text-primary" />
              <span className="text-foreground/85">Direct email:</span>
              <a
                href={`mailto:${profile.email}`}
                className="text-primary hover:underline"
                data-testid="instructor-email-link"
              >
                {profile.email}
              </a>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default InstructorProfile;
